import { useMutation } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiClientError, api } from "../lib/api";
import { money } from "../lib/format";
import i18n from "../lib/i18n";

interface InvoiceLineRow {
  key: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

interface CreatedInvoice {
  invoice: { id: string };
}

function emptyLine(): InvoiceLineRow {
  return { key: crypto.randomUUID(), description: "", quantity: "1", unitPrice: "" };
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function InvoiceNew() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const [, setLocation] = useLocation();

  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [issueDate, setIssueDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState("");
  const [taxPercent, setTaxPercent] = useState("0");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<InvoiceLineRow[]>([emptyLine()]);
  const [formError, setFormError] = useState<string | null>(null);

  const updateLine = (key: string, patch: Partial<InvoiceLineRow>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (key: string) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));

  const subtotal = lines.reduce((sum, l) => {
    const qty = Number(l.quantity);
    const price = Number(l.unitPrice);
    return sum + (Number.isFinite(qty) && Number.isFinite(price) ? qty * price : 0);
  }, 0);
  const taxAmount = subtotal * (Number(taxPercent || "0") / 100);
  const total = subtotal + taxAmount;

  const createMutation = useMutation({
    mutationFn: () =>
      api<CreatedInvoice>("/api/v1/invoices", {
        method: "POST",
        body: JSON.stringify({
          clientName: clientName.trim(),
          clientEmail: clientEmail.trim() || undefined,
          clientAddress: clientAddress.trim() || undefined,
          currency,
          issueDate,
          dueDate: dueDate || undefined,
          taxPercent: Number(taxPercent || "0"),
          notes: notes.trim() || undefined,
          lineItems: lines
            .filter((l) => l.description.trim())
            .map((l) => ({
              description: l.description.trim(),
              quantity: Number(l.quantity),
              unitPrice: Number(l.unitPrice),
            })),
        }),
      }),
    onSuccess: (created) => setLocation(`/invoices/${created.invoice.id}`),
    onError: (err) => {
      setFormError(err instanceof ApiClientError ? err.message : t("invoices:form.createFailed"));
    },
  });

  const canSubmit =
    clientName.trim().length > 0 &&
    lines.some((l) => l.description.trim() && Number(l.quantity) > 0 && Number(l.unitPrice) >= 0);

  return (
    <>
      <div className="mb-6">
        <h1>{t("invoices:form.title")}</h1>
      </div>

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          if (!canSubmit) {
            setFormError(t("invoices:form.atLeastOneLine"));
            return;
          }
          createMutation.mutate();
        }}
      >
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("invoices:form.clientSection")}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="inv-client-name">{t("invoices:form.clientName")}</Label>
              <Input
                id="inv-client-name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="inv-client-email">{t("invoices:form.clientEmail")}</Label>
              <Input
                id="inv-client-email"
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="inv-client-address">{t("invoices:form.clientAddress")}</Label>
              <Textarea
                id="inv-client-address"
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="inv-issue-date">{t("invoices:form.issueDate")}</Label>
              <Input
                id="inv-issue-date"
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="inv-due-date">{t("invoices:form.dueDate")}</Label>
              <Input
                id="inv-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="inv-currency">{t("invoices:form.currency")}</Label>
              <Input
                id="inv-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                maxLength={3}
                className="mt-1 uppercase"
              />
            </div>
            <div>
              <Label htmlFor="inv-tax-percent">{t("invoices:form.taxPercent")}</Label>
              <Input
                id="inv-tax-percent"
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={taxPercent}
                onChange={(e) => setTaxPercent(e.target.value)}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">{t("invoices:form.lineItems")}</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addLine}>
              <Plus className="h-4 w-4" aria-hidden />
              {t("invoices:form.addLine")}
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {lines.map((line) => {
              const lineTotal = Number(line.quantity) * Number(line.unitPrice);
              return (
                <div
                  key={line.key}
                  className="grid grid-cols-1 gap-2 rounded-md border p-3 sm:grid-cols-[1fr_5rem_7rem_7rem_2.5rem] sm:items-end sm:gap-3"
                >
                  <div>
                    <Label htmlFor={`line-desc-${line.key}`} className="sr-only sm:not-sr-only">
                      {t("invoices:form.description")}
                    </Label>
                    <Input
                      id={`line-desc-${line.key}`}
                      value={line.description}
                      onChange={(e) => updateLine(line.key, { description: e.target.value })}
                      placeholder={t("invoices:form.description")}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`line-qty-${line.key}`} className="sr-only sm:not-sr-only">
                      {t("invoices:form.quantity")}
                    </Label>
                    <Input
                      id={`line-qty-${line.key}`}
                      type="number"
                      min={0}
                      step="1"
                      value={line.quantity}
                      onChange={(e) => updateLine(line.key, { quantity: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`line-price-${line.key}`} className="sr-only sm:not-sr-only">
                      {t("invoices:form.unitPrice")}
                    </Label>
                    <Input
                      id={`line-price-${line.key}`}
                      type="number"
                      min={0}
                      step="0.01"
                      value={line.unitPrice}
                      onChange={(e) => updateLine(line.key, { unitPrice: e.target.value })}
                    />
                  </div>
                  <div className="text-right text-sm tabular-nums sm:pb-2.5">
                    {Number.isFinite(lineTotal)
                      ? money(lineTotal.toFixed(2), currency, locale)
                      : "—"}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t("invoices:form.removeLine")}
                    disabled={lines.length === 1}
                    onClick={() => removeLine(line.key)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 sm:grid-cols-2">
            <div>
              <Label htmlFor="inv-notes">{t("invoices:form.notes")}</Label>
              <Textarea
                id="inv-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("invoices:form.notesPlaceholder")}
                className="mt-1"
                rows={4}
              />
            </div>
            <div className="space-y-1 text-sm sm:justify-self-end sm:text-right">
              <div className="flex justify-between gap-8 sm:justify-end">
                <span className="text-muted-foreground">{t("invoices:form.subtotal")}</span>
                <span className="tabular-nums">{money(subtotal.toFixed(2), currency, locale)}</span>
              </div>
              <div className="flex justify-between gap-8 sm:justify-end">
                <span className="text-muted-foreground">{t("invoices:form.tax")}</span>
                <span className="tabular-nums">
                  {money(taxAmount.toFixed(2), currency, locale)}
                </span>
              </div>
              <div className="flex justify-between gap-8 text-base font-semibold sm:justify-end">
                <span>{t("invoices:form.total")}</span>
                <span className="tabular-nums">{money(total.toFixed(2), currency, locale)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {formError && <p className="text-sm text-destructive">{formError}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setLocation("/invoices")}>
            {t("invoices:form.cancel")}
          </Button>
          <Button type="submit" disabled={!canSubmit || createMutation.isPending}>
            {createMutation.isPending ? t("invoices:form.creating") : t("invoices:form.create")}
          </Button>
        </div>
      </form>
    </>
  );
}
